import { useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth.tsx';
import { AppearanceControls } from './AppearanceControls.tsx';

export function Topbar() {
  return (
    <header className="topbar">
      <div className="shell topbar__inner">
        <Link to="/" className="brand">
          <BrandMark />
          <span>
            Last Stream Standing
            <small>Live vom Event</small>
          </span>
        </Link>
        <AccountNav />
      </div>
    </header>
  );
}

/** Das Fadenkreuz im roten Quadrat -- Kopfleiste und Passwortseite. */
export function BrandMark() {
  return (
    <span className="brand__mark">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
        <circle cx="8" cy="8" r="3" fill="currentColor" />
        <path d="M8 1v2M8 13v2M1 8h2M13 8h2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    </span>
  );
}

function AccountNav() {
  const { user, logout, loading } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const next = encodeURIComponent(location.pathname + location.search);

  return (
    <nav className="topbar__nav">
      <NavLink to="/" end className={({ isActive }) => (isActive ? 'topbar__home is-active' : 'topbar__home')}>
        Events
      </NavLink>
      {user && (
        <NavLink to="/events/new" className={({ isActive }) => (isActive ? 'is-active' : '')}>
          Anlegen
        </NavLink>
      )}
      <AppearanceControls />
      {loading ? null : user ? (
        <div className="account">
          <button type="button" className="account__button" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
            <span className="account__avatar">{user.displayName.slice(0, 1).toUpperCase()}</span>
            <span className="account__name">{user.displayName}</span>
          </button>
          {open && (
            <>
              <button type="button" className="account__scrim" aria-label="Schließen" onClick={() => setOpen(false)} />
              <div className="account__menu">
                <p className="account__mail">{user.email}</p>
                <Link to="/events/new" onClick={() => setOpen(false)}>
                  Event anlegen
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    void logout();
                  }}
                >
                  Abmelden
                </button>
              </div>
            </>
          )}
        </div>
      ) : (
        <Link className="btn btn--sm" to={`/anmelden?weiter=${next}`}>
          Anmelden
        </Link>
      )}
    </nav>
  );
}

export function Footer() {
  return (
    <footer className="footer">
      <div className="shell footer__inner">
        <span>Last Stream Standing — Testphase</span>
        <LegalLinks />
      </div>
    </footer>
  );
}

/**
 * Impressum und Datenschutz -- unten auf jeder Seite.
 *
 * `plain` fuer die Passwortseite, die keinen Router hat. `newTab` fuer Regie,
 * Senden und Zuschauen: dort wuerde ein Wechsel der Seite die Kamera oder den
 * laufenden Stream beenden -- das Impressum geht deshalb in einem neuen Tab auf.
 */
export function LegalLinks({ plain = false, newTab = false }: { plain?: boolean; newTab?: boolean }) {
  const links = [
    ['/impressum', 'Impressum'],
    ['/datenschutz', 'Datenschutz'],
  ] as const;
  return (
    <nav className="legal-links" aria-label="Rechtliches">
      {links.map(([to, label]) =>
        plain || newTab ? (
          <a key={to} href={to} {...(newTab ? { target: '_blank', rel: 'noopener' } : {})}>
            {label}
          </a>
        ) : (
          <Link key={to} to={to}>
            {label}
          </Link>
        ),
      )}
    </nav>
  );
}

/** Dieselben Links in den dunklen Ansichten (Regie, Senden, Zuschauen) -- schmal, ganz unten. */
export function StageLegal() {
  return (
    <div className="stage-legal">
      <LegalLinks newTab />
    </div>
  );
}

export function ConnPill({ status }: { status: 'connecting' | 'open' | 'closed' }) {
  const cls = status === 'open' ? 'conn conn--on' : status === 'closed' ? 'conn conn--off' : 'conn';
  const label = status === 'open' ? 'verbunden' : status === 'closed' ? 'Verbindung weg' : 'verbinde …';
  return (
    <span className={cls} title="WebSocket-Verbindung zur Regie">
      <span className="conn__dot" />
      {label}
    </span>
  );
}

/* Pfeil fuer Klappknoepfe -- Regie und Zuschauersicht klappen ihre Spalten gleich. */
const CHEVRON = { left: 'M8.5 3 4.5 7l4 4', right: 'M5.5 3l4 4-4 4', down: 'M3 5.5l4 4 4-4' } as const;

export function Chevron({ dir }: { dir: keyof typeof CHEVRON }) {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden>
      <path
        d={CHEVRON[dir]}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
